import { Link } from 'react-router-dom';
import { Container } from '../ui/Container';
import { Facebook, Twitter, Linkedin, Instagram, MapPin, Phone, Mail } from 'lucide-react';
import { useState, HTMLAttributes } from 'react';
import { Button } from '../ui/Button';
import { useTranslation } from 'react-i18next';
import { newsletterService } from '../../services/newsletter';

export function Footer({ className = '', ...props }: HTMLAttributes<HTMLElement>) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  
  const navigation = {
    solutions: [
      { name: t('footer.solutionsPME') || 'Gestion des PME', href: './' },
      { name: t('footer.portfolioManagement') || 'Gestion de portefeuille', href: './' },
      { name: t('nav.pricing'), href: '/tarification' },
    ],
    support: [
      { name: t('footer.documentation'), href: '/under-development' },
      { name: t('footer.learn') || 'Apprendre', href: '/under-development' },
      { name: t('nav.faq'), href: '/faq' },
      { name: t('footer.contact'), href: '/under-development' },
    ],
    company: [
      { name: t('footer.aboutUs'), href: '/about' },
      { name: t('footer.blog'), href: '/under-development' },
      { name: t('footer.team'), href: '/team' },
      { name: t('footer.careers'), href: '/under-development' },
      { name: t('footer.partners') || 'Partenaires', href: '/under-development' },
    ],
    legal: [
      { name: t('footer.privacy'), href: '/under-development' },
      { name: 'Conditions d\'utilisation', href: '/terms' },
    ],
    social: [
      { name: 'Facebook', href: '#', icon: Facebook },
      { name: 'Twitter', href: '#', icon: Twitter },
      { name: 'LinkedIn', href: 'https://www.linkedin.com/company/i-kiotahub/', icon: Linkedin },
      { name: 'Instagram', href: '#', icon: Instagram },
    ],
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubscriptionStatus('loading');

    try {
      await newsletterService.subscribe(email);
      setSubscriptionStatus('success');
      setEmail('');
    } catch (error) {
      setSubscriptionStatus('error');
    }
  };

  return (
    <footer className={`bg-gray-900 ${className}`} aria-labelledby="footer-heading" {...props}>
      <h2 id="footer-heading" className="sr-only">Footer</h2>
      <Container className="pb-8 pt-16 sm:pt-24">
        <div className="xl:grid xl:grid-cols-3 xl:gap-8">
          <div className="space-y-8">
            <Link to="/" className="text-white text-2xl font-bold">
              i-kiotaHub
            </Link>
            <p className="text-sm leading-6 text-gray-300">
              {t('footer.tagline')}
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-gray-300">
                <MapPin className="h-5 w-5" />
                <span className="text-sm">RDC/Nord-Kivu/Goma, Commune de Goma, Quartier Lac Vert, Avenue Kabanda</span>
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <Phone className="h-5 w-5" />
                <span className="text-sm">+243 979 588 462</span>
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <Mail className="h-5 w-5" />
                <span className="text-sm">ikiota@ikiotahub.com</span>
              </div>
            </div>
            <div className="space-y-2 text-sm text-gray-400">
              <p>RCCM: CD/GOMA/RCCM/23-B-00196</p>
              <p>ID NAT: 19-H5300-N40995F</p>
              <p>NIF: A2321658S</p>
            </div>
            
          </div>

          <div className="mt-16 grid grid-cols-2 gap-8 xl:col-span-2 xl:mt-0">
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div>
                <h3 className="text-sm font-semibold leading-6 text-white">{t('footer.solutions') || 'Solutions'}</h3>
                <ul role="list" className="mt-6 space-y-4">
                  {navigation.solutions.map((item) => (
                    <li key={item.name}>
                      <Link to={item.href} className="text-sm leading-6 text-gray-300 hover:text-white">
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-10 md:mt-0">
                <h3 className="text-sm font-semibold leading-6 text-white">{t('footer.support')}</h3>
                <ul role="list" className="mt-6 space-y-4">
                  {navigation.support.map((item) => (
                    <li key={item.name}>
                      <Link to={item.href} className="text-sm leading-6 text-gray-300 hover:text-white">
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div>
                <h3 className="text-sm font-semibold leading-6 text-white">{t('footer.company')}</h3>
                <ul role="list" className="mt-6 space-y-4">
                  {navigation.company.map((item) => (
                    <li key={item.name}>
                      <Link to={item.href} className="text-sm leading-6 text-gray-300 hover:text-white">
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-10 md:mt-0">
                <h3 className="text-sm font-semibold leading-6 text-white">{t('footer.legal')}</h3>
                <ul role="list" className="mt-6 space-y-4">
                  {navigation.legal.map((item) => (
                    <li key={item.name}>
                      <Link to={item.href} className="text-sm leading-6 text-gray-300 hover:text-white">
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 border-t border-white/10 pt-8">
          <h3 className="text-sm font-semibold leading-6 text-white">
            {t('footer.newsletter.title')}
          </h3>
          <p className="mt-2 text-sm leading-6 text-gray-300">
            {t('footer.newsletter.description')}
          </p>
          <form onSubmit={handleSubscribe} className="mt-6 sm:flex sm:max-w-md gap-x-4">
            <label htmlFor="email-address" className="sr-only">
              {t('footer.newsletter.emailLabel')}
            </label>
            <input
              type="email"
              name="email-address"
              id="email-address"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full min-w-0 appearance-none rounded-md border-0 bg-white/5 px-3 py-1.5 text-base text-white shadow-sm ring-1 ring-inset ring-white/10 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:w-64 sm:text-sm sm:leading-6"
              placeholder={t('footer.newsletter.emailPlaceholder')}
            />
            <Button
              type="submit"
              disabled={subscriptionStatus === 'loading'}
              className="mt-4 sm:mt-0"
            >
              {subscriptionStatus === 'loading' ? t('footer.newsletter.subscribing') : t('footer.newsletter.subscribe')}
            </Button>
          </form>
          {subscriptionStatus === 'success' && (
            <p className="mt-4 text-sm text-green-400">
              {t('footer.newsletter.successMessage')}
            </p>
          )}
          {subscriptionStatus === 'error' && (
            <p className="mt-4 text-sm text-red-400">
              {t('footer.newsletter.errorMessage')}
            </p>
          )}
        </div>

        <div className="mt-8 border-t border-white/10 pt-8 md:flex md:items-center md:justify-between">
          <div className="flex space-x-6 md:order-2">
            {navigation.social.map((item) => (
              <Link
                key={item.name}
                to="/under-development"
                className="text-gray-400 hover:text-gray-300"
              >
                <span className="sr-only">{item.name}</span>
                <item.icon className="h-6 w-6" aria-hidden="true" />
              </Link>
            ))}
          </div>
          <p className="mt-8 text-xs leading-5 text-gray-400 md:order-1 md:mt-0">
            &copy; {new Date().getFullYear()} i-kiotaHub. {t('footer.allRightsReserved')}
          </p>
        </div>
      </Container>
    </footer>
  );
}